import { Typography } from '@/components/UI/Typography'
import { Modal } from '@mantine/core'
import type React from 'react'
import { memo } from 'react'
import styles from './Dialog.module.css'

type Props = {
  children?: React.ReactNode
  title?: string
  /** 閉じるボタンを表示するか（default: true） */
  showCloseButton?: boolean
  /** コンパクトポップアップスタイルを使用するか */
  compact?: boolean
  /** @deprecated Mantineがヘッダー右端に閉じるボタンを配置するため不要。API互換のために残している */
  closeButtonInline?: boolean
  selectorState?: boolean
  closeAction?: () => void
  closeFunc?: () => void
  /** モーダルが開いている間、bodyのスクロールをロックするか（Mantineの lockScroll に対応）。Storybook など埋め込み環境では false にする */
  lockScroll?: boolean
  /** モーダル外側クリックで閉じるか（default: true）。旧 c-dialog はオーバーレイに onClick がなく閉じなかった */
  closeOnClickOutside?: boolean
  /** Mantine Modal の z-index を上書きする。高い z-index を持つ要素の上に表示する場合に使用 */
  zIndex?: number
}

export const Dialog = memo(
  ({
    children,
    title,
    showCloseButton = true,
    compact = false,
    // closeButtonInline は deprecated。Mantine が自動でヘッダー右端に配置するため不要
    selectorState,
    closeAction,
    closeFunc,
    lockScroll,
    closeOnClickOutside = true,
    zIndex,
  }: Props) => {
    const isOpen = selectorState ?? false

    const handleClose = () => {
      closeFunc?.()
      closeAction?.()
    }

    return (
      <Modal
        opened={isOpen}
        onClose={handleClose}
        title={
          title ? (
            <Typography variant={'subtitle1'} c={'black'}>
              {title}
            </Typography>
          ) : undefined
        }
        // 横は16pxに、縦は24pxにする。そのため、ベースとなるpaddingは `16px` にする。
        padding={'16px'}
        centered
        withCloseButton={showCloseButton}
        closeButtonProps={{ 'aria-label': '閉じる', className: styles.closeButton }}
        size={compact ? 'sm' : 'md'}
        radius="lg"
        lockScroll={lockScroll}
        closeOnClickOutside={closeOnClickOutside}
        zIndex={zIndex}
        styles={{
          header: {
            // 上のpaddingを `24px` にする
            paddingTop: 24,
          },
          title: {
            fontWeight: 700,
            textAlign: 'center',
            width: '100%',
            color: '#4b5563',
            paddingLeft: showCloseButton ? 28 : 0,
          },
          body: {
            // 下のpaddingを `24px` にする
            paddingBottom: 24,
          },
          close: { '& svg': { strokeWidth: 3 } },
        }}
      >
        {children}
      </Modal>
    )
  },
)
